// Adding an expense.
//
// This is the one screen that has to work for somebody who cannot read either
// language on it. So the pictures are the controls and the words are only
// captions: a camera, a microphone, digits, a cup of tea, a spanner. Somebody
// who has been shown it once should be able to work it without reading a word.
//
//   1. Photograph the bill      — the evidence, and the audit trail
//   2. Say what it was for      — the meaning, and usually the reliable number
//   3. Tap the amount           — digits are the same in every language
//   4. Tap what it was for      — a picture, not a dropdown of words
//   5. Send                     — one large green button
//
// Both captured inputs go to the model together. A handwritten slip from a
// local repair shop is often unreadable no matter how good the OCR is, while
// the spoken "teen sau rupaye" is unambiguous — so when the two disagree the
// spoken figure wins, and the screen says so.
//
// The photograph carries real weight here: with one, a claim can be recorded
// without a payee being typed at all, and the approver reads the name off the
// bill. Nobody standing in a shop is blocked because they cannot spell.

import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import { inr, today } from '../lib/fmt';
import { preparePhoto, bytes } from '../lib/image';
import { categoryIcon, accountIcon } from '../lib/icons';
import type { ClaimRow, Extraction } from '../lib/types';
import {
  Button, Card, Field, Input, Select, Note, Pill, Table, Th, Td,
  TableWrap, Tag, cx,
} from '../components/ui';
import { Loading, CardSkeleton, TableSkeleton } from '../components/skeleton';

const T = {
  en: {
    title: 'Add an expense',
    step1: 'Photograph the bill', step1done: 'Bill photographed',
    step2: 'Say what it was for', step2sub: 'Hindi or English',
    step3: 'How much?', step4: 'What was it for?', step5: 'Paid from',
    retake: 'Take another', listening: 'Listening…', stop: 'Stop',
    choose: 'Choose a file', chooseSub: 'Photo or PDF, from this device',
    speak: 'Say it', type: 'Type it', typeHint: 'What was this for?',
    reading: 'Reading the bill and what you said…',
    amount: 'Amount paid', paidTo: 'Paid to', what: 'Note',
    when: 'Date', category: 'Category', gstBill: 'Proper GST invoice',
    submit: 'Send', sent: 'Sent',
    details: 'More details', recent: 'Your recent claims',
    heard: 'What you said', noAmount: 'Tap the amount first.',
    noProof: 'Photograph the bill, or type who was paid.',
    saved: 'saved',
  },
  hi: {
    title: 'खर्चा जोड़ें',
    step1: 'बिल की फोटो लें', step1done: 'फोटो हो गई',
    step2: 'बोलकर बताएं', step2sub: 'हिंदी या अंग्रेज़ी',
    step3: 'कितने रुपये?', step4: 'किस चीज़ के लिए?', step5: 'कहाँ से दिया',
    retake: 'दूसरी फोटो', listening: 'सुन रहे हैं…', stop: 'रोकें',
    choose: 'फ़ाइल चुनें', chooseSub: 'फोटो या PDF',
    speak: 'बोलें', type: 'लिखें', typeHint: 'किस चीज़ के लिए?',
    reading: 'बिल और आपकी बात पढ़ी जा रही है…',
    amount: 'कितने रुपये', paidTo: 'किसको दिया', what: 'नोट',
    when: 'तारीख', category: 'श्रेणी', gstBill: 'पक्का बिल (GST)',
    submit: 'भेजें', sent: 'भेज दिया',
    details: 'और जानकारी', recent: 'आपके पिछले खर्चे',
    heard: 'आपने कहा', noAmount: 'पहले रकम दबाएँ',
    noProof: 'बिल की फोटो लें, या किसको दिया वो लिखें',
    saved: 'बचा',
  },
};

type SpeechCtor = new () => SpeechRecognitionLike;
interface SpeechRecognitionLike {
  lang: string; interimResults: boolean; continuous: boolean;
  start(): void; stop(): void;
  onresult: ((e: { resultIndex: number; results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
}

type Photo = { url: string; id: string; name: string; pdf: boolean;
               before: number; after: number; compressed: boolean };

export default function Capture() {
  const { user, meta, can, refresh } = useSession();
  // English by default; whoever needs Hindi switches it here and it sticks to
  // their account. The person who most needs Hindi is the least able to ask an
  // administrator for it in English, so it cannot live only in the admin screen.
  const [lang, setLang] = useState<'en' | 'hi'>(user?.language === 'hi' ? 'hi' : 'en');
  const t = T[lang];

  async function chooseLanguage(next: 'en' | 'hi') {
    setLang(next);
    try {
      await api.post('/api/auth/language', { language: next });
      await refresh();
    } catch { /* the screen has already switched; it just will not persist */ }
  }
  const ai = meta?.ai;
  const categories = (meta?.categories ?? []).filter((c) => c.kind === 'expense');
  const accounts = meta?.accounts ?? [];

  const [photo, setPhoto] = useState<Photo | null>(null);
  const [shrinking, setShrinking] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interim, setInterim] = useState('');
  const [listening, setListening] = useState(false);
  const [reading, setReading] = useState(false);
  const [extraction, setExtraction] = useState<Extraction | null>(null);
  const [status, setStatus] = useState<{ tone: 'note' | 'critical'; title: string; body: string } | null>(null);

  const [amount, setAmount] = useState('');
  const [vendor, setVendor] = useState('');
  const [what, setWhat] = useState('');
  const [when, setWhen] = useState(today());
  const [category, setCategory] = useState('');
  const [account, setAccount] = useState('');
  const [gstBill, setGstBill] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [claims, setClaims] = useState<ClaimRow[]>([]);
  // The recent list is fetched, so the screen owes the person its shape while
  // it is in flight — on a phone this is the slowest part of the page.
  const [loadingRecent, setLoadingRecent] = useState(true);

  const fileRef = useRef<HTMLInputElement>(null);
  const pickRef = useRef<HTMLInputElement>(null);
  const recogRef = useRef<SpeechRecognitionLike | null>(null);
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    if (!category && categories[0]) setCategory(categories[0]._id);
    if (!account) setAccount((accounts.find((a) => a.kind === 'cash') ?? accounts[0])?.id ?? '');
  }, [categories, accounts, category, account]);

  const loadRecent = () =>
    api.get<{ claims: ClaimRow[] }>('/api/claims', { limit: 12 })
       .then((d) => setClaims(d.claims)).catch(() => {})
       .finally(() => setLoadingRecent(false));
  useEffect(() => { void loadRecent(); }, []);

  // ── step 1: the photograph ──────────────────────────────────
  // Shrunk in the browser before it is sent: a phone bill photo is several
  // megabytes, and the upload happens on a phone connection, in a shop.
  async function onPhoto(file: File) {
    setStatus(null);
    setShrinking(true);
    try {
      const prepared = await preparePhoto(file);
      const url = URL.createObjectURL(prepared.blob);
      const r = await api.upload<{ attachment_id: string }>('/api/capture/photo', prepared.blob);
      setPhoto({
        url, id: r.attachment_id,
        name: file.name || 'bill', pdf: file.type === 'application/pdf',
        before: prepared.before, after: prepared.after, compressed: prepared.compressed,
      });
    } catch (e) {
      setStatus({ tone: 'critical', title: 'That did not upload',
                  body: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setShrinking(false);
    }
  }

  // ── step 2: the voice note ──────────────────────────────────
  const SR = (window as unknown as { SpeechRecognition?: SpeechCtor; webkitSpeechRecognition?: SpeechCtor });
  const Recognition = SR.SpeechRecognition ?? SR.webkitSpeechRecognition;

  function toggleListening() {
    if (listening) { recogRef.current?.stop(); return; }
    if (!Recognition) {
      setStatus({ tone: 'critical', title: 'This browser cannot listen',
                  body: 'Use Chrome, or tap the amount below.' });
      return;
    }
    const r = new Recognition();
    recogRef.current = r;
    r.lang = lang === 'hi' ? 'hi-IN' : 'en-IN';
    r.interimResults = true;
    r.continuous = true;

    let heard = transcript ? transcript + ' ' : '';
    r.onresult = (e) => {
      let live = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i];
        const text = res?.[0]?.transcript ?? '';
        if (res?.isFinal) heard += text + ' '; else live += text;
      }
      setTranscript(heard.trim());
      setInterim(live);
    };
    r.onerror = (e) => {
      setStatus({ tone: 'critical', title: 'Could not hear that',
        body: e.error === 'not-allowed'
          ? 'Microphone permission was refused. Allow it, or tap the amount.'
          : String(e.error) });
    };
    r.onend = () => { setListening(false); setInterim(''); recogRef.current = null; };
    r.start();
    setListening(true);
    setStatus(null);
  }

  async function read() {
    if (!photo && !transcript.trim()) return;
    if (!ai?.enabled) {
      if (transcript.trim() && !what) setWhat(transcript.trim());
      return;
    }
    setReading(true);
    setStatus(null);
    try {
      const r = await api.post<{ extracted: Extraction }>('/api/capture/read', {
        attachment_id: photo?.id ?? null,
        transcript: transcript.trim() || null,
      });
      const x = r.extracted;
      setExtraction(x);
      if (x.amount) setAmount(String(Math.round(x.amount / 100)));
      if (x.vendor) setVendor(x.vendor);
      if (x.description) setWhat(x.description);
      if (x.spent_on) setWhen(x.spent_on);
      if (x.has_tax_invoice) setGstBill(true);
      const match = categories.find((c) => c.name.toLowerCase() === x.category.toLowerCase());
      if (match) setCategory(match._id);
    } catch (e) {
      if (transcript.trim() && !what) setWhat(transcript.trim());
      setStatus({ tone: 'critical', title: 'Could not read that automatically',
                  body: `${e instanceof ApiError ? e.message : String(e)} Tap the amount below.` });
    } finally {
      setReading(false);
    }
  }

  useEffect(() => {
    if (listening || reading) return;
    if (!photo && !transcript.trim()) return;
    void read();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photo?.id, listening]);

  // ── the keypad ──────────────────────────────────────────────
  const tapDigit = (d: string) =>
    setAmount((a) => {
      const next = (a === '0' ? '' : a) + d;
      return next.replace(/^0+(?=\d)/, '').slice(0, 9);
    });
  const backspace = () => setAmount((a) => a.slice(0, -1));

  const rupees = Number(amount) || 0;
  const canSend = rupees > 0 && (Boolean(photo) || Boolean(vendor.trim()));

  async function submit() {
    const paise = Math.round(rupees * 100);
    if (!(paise > 0)) { setStatus({ tone: 'critical', title: t.noAmount, body: '' }); return; }
    if (!photo && !vendor.trim()) { setStatus({ tone: 'critical', title: t.noProof, body: '' }); return; }

    setBusy(true);
    try {
      const claim = await api.post<ClaimRow>('/api/claims', {
        spent_on: when,
        vendor_text: vendor.trim() || null,
        category: category || null,
        description: what.trim() || null,
        amount: paise,
        has_tax_invoice: gstBill,
        paid_from: account || null,
        mode: accounts.find((a) => a.id === account)?.kind === 'cash' ? 'cash' : 'neft',
        capture_kind: extraction?.capture_kind ?? (photo ? 'photo' : transcript ? 'voice' : 'manual'),
        ai: extraction ?? null,
        ai_confidence: extraction?.confidence ?? null,
        ai_model: extraction?.ai_model ?? null,
        transcript: transcript.trim() || null,
        attachments: photo ? [photo.id] : [],
      });
      setStatus({ tone: 'note', title: t.sent, body: `${claim.claim_no} · ${inr(claim.amount)}` });
      setPhoto(null); setTranscript(''); setExtraction(null);
      setAmount(''); setVendor(''); setWhat(''); setGstBill(false);
      if (fileRef.current) fileRef.current.value = '';
      await loadRecent();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setStatus({ tone: 'critical', title: 'Not sent',
                  body: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  const lowConfidence = extraction != null && extraction.confidence < 0.7;

  return (
    <div className="max-w-[640px] mx-auto pb-4">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="min-w-0">
          <h1 className="font-display text-[24px] font-bold tracking-[-0.02em] mb-1">{t.title}</h1>
          <p className="text-[13px] text-muted">{user?.name}</p>
        </div>
        {/* Two letters rather than two words: somebody who cannot read the
            screen can still recognise the script they do read. */}
        <div className="flex shrink-0 rounded-lg border border-rule overflow-hidden" role="group"
             aria-label="Screen language">
          {([['en', 'A', 'English'], ['hi', 'अ', 'हिंदी']] as const).map(([code, glyph, name]) => (
            <button key={code} type="button" onClick={() => void chooseLanguage(code)}
              aria-pressed={lang === code} title={name}
              className={cx('w-12 min-h-[44px] font-display text-[18px] font-semibold cursor-pointer',
                lang === code ? 'bg-brand text-brand-ink' : 'bg-card text-ink-2 hover:bg-card-2')}>
              {glyph}
            </button>
          ))}
        </div>
      </div>

      {/* Only shown to somebody who could actually do something about it. The
          person recording petty spend cannot set an API key, and a paragraph of
          English above the camera is noise to them. */}
      {!ai?.enabled && can('expense:read_all') && (
        <Note title="Reading bills is switched off">
          {ai?.reason} Photos and voice notes are still saved — only the automatic reading is off.
        </Note>
      )}

      {status && (
        <Note title={status.title} tone={status.tone === 'critical' ? 'critical' : undefined}>
          {status.body}
        </Note>
      )}

      {/* ── 1 · photograph ──────────────────────────────────── */}
      <Step n={1} title={photo ? t.step1done : t.step1} done={Boolean(photo)}>
        {/* Two inputs, deliberately. `capture` opens the camera straight away,
            which is right standing in a shop and wrong at a desk — the
            accountant is usually attaching a supplier's PDF. */}
        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
               onChange={(e) => { const f = e.target.files?.[0]; if (f) void onPhoto(f); }} />
        <input ref={pickRef} type="file" accept="image/*,application/pdf" className="hidden"
               onChange={(e) => { const f = e.target.files?.[0]; if (f) void onPhoto(f); }} />
        {photo ? (
          <div className="flex items-start gap-4">
            {photo.pdf ? (
              <div className="w-28 h-28 grid place-items-center rounded-lg border border-rule bg-card-2">
                <span className="text-[34px] leading-none">📄</span>
              </div>
            ) : (
              <img src={photo.url} alt="" className="w-28 h-28 object-cover rounded-lg border border-rule" />
            )}
            <div className="flex flex-col gap-2 min-w-0">
              <span className="font-mono text-[11px] text-muted truncate">{photo.name}</span>
              {photo.compressed ? (
                <span className="font-mono text-[11px] text-muted">
                  {bytes(photo.before)} → <b className="text-good">{bytes(photo.after)}</b>
                </span>
              ) : (
                <span className="font-mono text-[11px] text-muted">{bytes(photo.after)}</span>
              )}
              <BigButton icon="🔁" label={t.retake} small onClick={() => pickRef.current?.click()} />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <BigButton icon={shrinking ? '⏳' : '📷'} label={shrinking ? '…' : t.step1}
                       onClick={() => fileRef.current?.click()} disabled={shrinking} />
            <BigButton icon="📄" label={t.choose} sub={t.chooseSub}
                       onClick={() => pickRef.current?.click()} disabled={shrinking} />
          </div>
        )}
      </Step>

      {/* ── 2 · voice ───────────────────────────────────────── */}
      <Step n={2} title={listening ? t.listening : t.step2} sub={t.step2sub}
            done={Boolean(transcript.trim())}>
        {/* Speaking is the fast path for somebody who cannot write; typing is
            the fast path for somebody who can. Both land in the same field. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <BigButton icon="🎙" label={listening ? t.stop : t.speak}
                     live={listening} onClick={toggleListening} />
          <BigButton icon="⌨️" label={t.type}
                     onClick={() => { setTyping(true); setListening(false); recogRef.current?.stop(); }} />
        </div>
        {typing && (
          <textarea
            autoFocus rows={3} value={transcript} placeholder={t.typeHint}
            onChange={(e) => setTranscript(e.target.value)}
            onBlur={() => { if (!transcript.trim()) setTyping(false); void read(); }}
            className="mt-2 w-full rounded-lg border border-rule bg-card px-3.5 py-3 text-[16px]
                       text-ink focus:outline-2 focus:-outline-offset-1 focus:outline-s1" />
        )}
        {!typing && (transcript || interim) && (
          <div className="mt-3 rounded-lg bg-card-2 px-4 py-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted m-0 mb-1">{t.heard}</p>
            <p className="m-0 text-[14px] text-ink">{transcript} <span className="text-muted">{interim}</span></p>
          </div>
        )}
        {reading && <p className="mt-2 text-[13px] text-muted">{t.reading}</p>}
      </Step>

      {/* ── 3 · amount ──────────────────────────────────────── */}
      <Step n={3} title={t.step3} done={rupees > 0}>
        <div className="rounded-xl border border-rule bg-card overflow-hidden">
          <div className="px-5 py-5 text-right border-b border-rule-soft">
            <span className="font-display text-[44px] leading-none font-semibold tracking-[-0.03em] tnum">
              <span className="text-muted">₹</span>{rupees ? rupees.toLocaleString('en-IN') : '0'}
            </span>
          </div>
          <div className="grid grid-cols-3">
            {['1','2','3','4','5','6','7','8','9'].map((d) => (
              <Key key={d} onClick={() => tapDigit(d)}>{d}</Key>
            ))}
            <Key onClick={() => tapDigit('00')}>00</Key>
            <Key onClick={() => tapDigit('0')}>0</Key>
            <Key onClick={backspace} muted aria-label="Delete">⌫</Key>
          </div>
        </div>
        {extraction && (
          <p className={cx('mt-2 text-[12.5px]', lowConfidence ? 'text-critical' : 'text-muted')}>
            {lowConfidence ? '⚠ ' : '✓ '}
            {extraction.amount_source === 'voice' ? 'From what you said'
              : extraction.amount_source === 'photo' ? 'From the bill'
              : extraction.amount_source === 'both' ? 'From the bill and what you said'
              : 'Not read clearly — check it'}
          </p>
        )}
      </Step>

      {/* ── 4 · category, as pictures ───────────────────────── */}
      <Step n={4} title={t.step4} done={Boolean(category)}>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {categories.map((c) => {
            const on = c._id === category;
            return (
              <button key={c._id} type="button" onClick={() => setCategory(c._id)}
                aria-pressed={on}
                className={cx(
                  'flex flex-col items-center justify-center gap-1.5 rounded-xl border px-1.5 py-3 min-h-[84px]',
                  'cursor-pointer transition-colors',
                  on ? 'border-brand bg-brand/10 ring-2 ring-brand' : 'border-rule bg-card hover:bg-card-2')}>
                <span className="text-[26px] leading-none">{categoryIcon(c.name, c.icon)}</span>
                <span className="text-[10.5px] leading-tight text-center text-ink-2 line-clamp-2">{c.name}</span>
              </button>
            );
          })}
        </div>
      </Step>

      {/* ── 5 · which pocket ────────────────────────────────── */}
      <Step n={5} title={t.step5} done={Boolean(account)} last>
        <div className="grid grid-cols-2 gap-2">
          {accounts.map((a) => {
            const on = a.id === account;
            return (
              <button key={a.id} type="button" onClick={() => setAccount(a.id)}
                aria-pressed={on}
                className={cx('flex items-center gap-2.5 rounded-xl border px-3 py-3 min-h-[56px] text-left',
                  'cursor-pointer transition-colors',
                  on ? 'border-brand bg-brand/10 ring-2 ring-brand' : 'border-rule bg-card hover:bg-card-2')}>
                <span className="text-[22px] leading-none shrink-0">{accountIcon(a.kind)}</span>
                <span className="text-[12.5px] leading-tight min-w-0 truncate">{a.name}</span>
              </button>
            );
          })}
        </div>
      </Step>

      {/* ── send ────────────────────────────────────────────── */}
      <button type="button" onClick={() => void submit()} disabled={busy || !canSend}
        className={cx('w-full flex items-center justify-center gap-3 rounded-xl px-5 min-h-[68px]',
          'font-display text-[19px] font-bold cursor-pointer transition-colors',
          canSend && !busy
            ? 'bg-good text-white hover:brightness-110'
            : 'bg-card-3 text-muted cursor-not-allowed')}>
        <span className="text-[26px] leading-none">{busy ? '⏳' : '✓'}</span>
        {t.submit}{rupees > 0 && ` · ₹${rupees.toLocaleString('en-IN')}`}
      </button>
      {!canSend && rupees > 0 && (
        <p className="mt-2 text-center text-[12.5px] text-muted">{t.noProof}</p>
      )}

      {/* ── everything a reader might still want ────────────── */}
      <button type="button" onClick={() => setShowDetails((v) => !v)}
        className="mt-5 w-full text-center text-[13px] text-ink-2 underline underline-offset-2 cursor-pointer min-h-[44px]">
        {showDetails ? '▲' : '▼'} {t.details}
      </button>

      {showDetails && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <Field label={t.paidTo}>
            <Input value={vendor} onChange={(e) => setVendor(e.target.value)}
                   placeholder={lang === 'hi' ? 'दुकान का नाम' : 'Tea stall, electrician, auto…'} />
          </Field>
          <Field label={t.when}>
            <Input type="date" value={when} onChange={(e) => setWhen(e.target.value)} />
          </Field>
          <Field label={t.what} span>
            <Input value={what} onChange={(e) => setWhat(e.target.value)} />
          </Field>
          <Field label={t.amount}>
            <Input type="number" step="0.01" inputMode="decimal" value={amount}
                   onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <Field label={t.category}>
            <Select value={category} onChange={(e) => setCategory(e.target.value)}>
              {categories.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
            </Select>
          </Field>
          <label className="flex items-center gap-2.5 text-[14px] text-ink-2 sm:col-span-2 min-h-[44px]">
            <input type="checkbox" checked={gstBill} onChange={(e) => setGstBill(e.target.checked)}
                   className="w-5 h-5" />
            {t.gstBill}
          </label>
        </div>
      )}

      {loadingRecent ? (
        <Loading label={t.recent}>
          <CardSkeleton flush sub={false} className="mt-6">
            <TableSkeleton rows={4} cols={4} />
          </CardSkeleton>
        </Loading>
      ) : claims.length > 0 && (
        <Card title={t.recent} flush className="mt-6">
          <TableWrap>
            <Table cards>
              <thead><tr>
                <Th>{t.when}</Th><Th>{t.paidTo}</Th><Th n>{t.amount}</Th><Th>Status</Th>
              </tr></thead>
              <tbody>
                {claims.map((c) => (
                  <tr key={c._id}>
                    <Td label="" className="font-mono text-[11.5px]">{c.spent_on}</Td>
                    <Td label="">{c.vendor?.name ?? c.vendor_text ?? '—'}
                      {c.attachment_count ? <Tag>{c.attachment_count} 📎</Tag> : null}</Td>
                    <Td label="" n>{inr(c.amount)}</Td>
                    <Td label="Status"><Pill kind={c.status}>{c.status}</Pill></Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </Card>
      )}
    </div>
  );
}

/** One big, unmistakable target. Large enough to hit without looking carefully. */
function BigButton(
  { icon, label, sub, onClick, live, small, disabled }:
  { icon: string; label: string; sub?: string; onClick: () => void;
    live?: boolean; small?: boolean; disabled?: boolean },
) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className={cx('w-full flex items-center justify-center gap-3 rounded-xl border cursor-pointer',
        'transition-colors disabled:opacity-60 disabled:cursor-wait',
        small ? 'px-4 min-h-[48px] text-[14px]' : 'px-5 py-4 min-h-[104px] text-[17px]',
        live ? 'border-critical bg-critical/10' : 'border-rule bg-card hover:bg-card-2')}>
      <span className={cx('leading-none shrink-0', small ? 'text-[20px]' : 'text-[34px]',
                          live && 'animate-pulse')}>
        {icon}
      </span>
      <span className="flex flex-col items-start min-w-0">
        <span className="font-semibold">{label}</span>
        {sub && <span className="text-[11.5px] font-normal text-muted leading-tight">{sub}</span>}
      </span>
    </button>
  );
}

/** A keypad key. Big enough for a thumb, per Apple's 44px guidance and then some. */
function Key({ children, onClick, muted, ...rest }:
  { children: React.ReactNode; onClick: () => void; muted?: boolean } & { 'aria-label'?: string }) {
  return (
    <button type="button" onClick={onClick} {...rest}
      className={cx('min-h-[58px] font-display text-[22px] font-semibold cursor-pointer',
        'border-r border-b border-rule-soft last:border-r-0 active:bg-card-3',
        muted ? 'text-muted hover:bg-card-2' : 'text-ink hover:bg-card-2')}>
      {children}
    </button>
  );
}

/** A numbered step. The number and the tick are the progress indicator. */
function Step(
  { n, title, sub, done, last, children }:
  { n: number; title: string; sub?: string; done: boolean; last?: boolean; children: React.ReactNode },
) {
  return (
    <section className="relative pl-11 pb-6">
      {!last && <span className="absolute left-[15px] top-9 bottom-1 w-px bg-rule" aria-hidden />}
      <span className={cx('absolute left-0 top-0 grid place-items-center w-8 h-8 rounded-full',
        'font-display font-semibold text-[15px] border',
        done ? 'bg-good border-good text-white' : 'bg-card border-rule text-ink-2')}>
        {done ? '✓' : n}
      </span>
      <div className="pt-0.5">
        <h2 className="font-display text-[16px] font-semibold m-0">{title}</h2>
        {sub && <p className="mt-0.5 mb-2.5 text-[12.5px] text-muted">{sub}</p>}
        <div className={sub ? '' : 'mt-2.5'}>{children}</div>
      </div>
    </section>
  );
}
